<template>
  <PageTemplate heading="Utleierprofiler">
    <InfoCard>
      En utleierprofil er leietakers motpart i leieforholdet; det være seg en privatperson, et ektepar eller et
      aksjeselskap. De som oppretter, og eier, utleierprofilen står fritt til å velge dens funksjon. Du kan samle så
      mange eller så få eiendommer og leieforhold som du selv ønsker på en utleierprofil. Se på det som en måte å styre
      din portefølje av leieforhold.
    </InfoCard>

    <BlockContainer class="p-5 mt-4">
      <div class="row align-items-center">
        <div v-if="isThereAProfessionalLandlord" class="col text-right">
          <button class="btn btn-sm btn-outline-secondary" @click="newLandlordModal = true">Ny utleierprofil</button>
        </div>
      </div>

      <List class="mt-5">
        <ListHeader v-if="!showListsItemsAsCards">
          <div class="col-4">Utleierprofil</div>

          <div class="col">Profileier</div>

          <div class="col-2 text-center">Leieobjekter</div>

          <div class="col-2 text-center">Leieforhold</div>

          <div class="col-1 mr-4">Se/Endre</div>
        </ListHeader>

        <router-link
          :to="{ name: 'landlord_profile_overview', params: { landlordId: landlord.id.toString() } }"
          v-for="(landlord, i) in getLandlordProfilesMeta"
          :key="i"
          class="text-decoration-none color-inherit"
          clickable
          @click.native="setLandlordId(landlord.id)"
        >
          <ListItemCard v-if="showListsItemsAsCards" :windowWidth="getWindowWidth" class="no-link-appearence">
            <div class="text-center pt-2">
              <NameInitials class="mr-2" :name="landlord.fullName"></NameInitials>
              <span>{{ landlord.fullName }}</span>
            </div>

            <div class="row mt-4 mb-2 py-3">
              <div class="col">
                <b class="text-secondary">Profileier</b>
                <p>{{ landlord.owner }}</p>
              </div>

              <div class="col">
                <b class="text-secondary">Leieobjekter</b>
                <p>{{ landlord.numberOfRentalObjects }}</p>
              </div>
            </div>

            <div class="row my-2">
              <div class="col">
                <b class="text-secondary">Leieforhold</b>
                <p>{{ landlord.numberOfTenancies }}</p>
              </div>

              <div class="col">
                <b class="text-secondary">Se/Endre</b>
                <p><i class="budicon-solid budicon-solid-eye-sight h3"></i></p>
              </div>
            </div>
          </ListItemCard>

          <ListItem v-else class="no-link-appearence">
            <div class="col-4">
              <div class="row align-items-center">
                <div class="col initials">
                  <NameInitials class="mr-2" :name="landlord.fullName"></NameInitials>
                </div>

                <div class="col d-flex align-items-center">
                  {{ landlord.fullName }}
                  <span v-if="landlord.id === defaultLandlordId" class="ml-3">
                    <i class="h4 budicon-star" />
                  </span>
                </div>
              </div>
            </div>

            <div class="col text-secondary">{{ landlord.owner }}</div>

            <div class="col-2 text-center text-secondary">{{ landlord.numberOfRentalObjects }}</div>

            <div class="col-2 text-center">{{ landlord.numberOfTenancies }}</div>

            <div class="col-1 text-right">
              <i class="h3 budicon-chevron-right"></i>
            </div>
          </ListItem>
        </router-link>
      </List>
    </BlockContainer>

    <!-- Add New Landlord Modal -->
    <hl-Modal
      v-if="newLandlordModal"
      class="text-center"
      iconClass="budicon-profile-picture text-primary"
      title="Opprett ny utleierprofil"
    >
      <ValidationObserver v-slot="{ invalid }">
        <p class="text-secondary text-left">
          Nye utleierprofiler kan kun opprettes for virksomheter. Ikke for privatpersoner.
        </p>

        <div class="mt-4">
          <hl-SearchCompanies :companyName="newLandlord.name" @companySet="populateCompany" />

          <div class="row mt-4">
            <div class="col">
              <ValidationProvider
                :name="translationCompanyName"
                :rules="validationRules.userProfile.newLandlordModal.companyName"
                v-slot="{ errors }"
              >
                <hl-Input
                  type="text"
                  :label="translationCompanyName"
                  v-model="newLandlord.name"
                  :error="errors[0]"
                  disabled
                ></hl-Input>
              </ValidationProvider>
            </div>

            <div class="col">
              <ValidationProvider
                name="Organisasjonsnummer"
                :rules="validationRules.userProfile.newLandlordModal.orgNumber"
                v-slot="{ errors }"
              >
                <hl-Input
                  type="text"
                  label="Organisasjonsnummer"
                  name="landlord.company.orgNumber"
                  v-model="newLandlord.organizationNumber"
                  :error="errors[0]"
                  disabled
                ></hl-Input>
              </ValidationProvider>
            </div>
          </div>

          <div class="mt-4">
            <ValidationProvider
              name="Adresse"
              :rules="validationRules.userProfile.newLandlordModal.street"
              v-slot="{ errors }"
            >
              <hl-Input
                type="text"
                placeholder="Eks: Utleieveien 2"
                label="Adresse"
                v-model="newLandlord.addresses[0].street"
                :error="errors[0]"
              />
            </ValidationProvider>
          </div>

          <div class="row mt-4">
            <div class="col">
              <ValidationProvider
                name="Postnummer"
                :rules="validationRules.userProfile.newLandlordModal.postalZipCode"
                v-slot="{ errors }"
              >
                <hl-Input
                  type="number"
                  placeholder="Eks: 0481"
                  label="Postnummer"
                  v-model="newLandlord.addresses[0].postalZipcode"
                  name="landlord.address.postalCode"
                  @input="fetchPostalArea"
                  ref="postalCode"
                  :error="errors[0]"
                />
              </ValidationProvider>
            </div>

            <div class="col">
              <ValidationProvider
                name="Poststed"
                :rules="validationRules.userProfile.newLandlordModal.postalArea"
                v-slot="{ errors }"
              >
                <hl-Input
                  type="text"
                  placeholder
                  label="Poststed"
                  v-model="newLandlord.addresses[0].postalArea"
                  name="landlord.address.postalArea"
                  disabled
                  :error="errors[0]"
                />
              </ValidationProvider>
            </div>
          </div>

          <!--<hl-Input
          class="mt-4"
          type="text"
          placeholder="alias"
          label="Alias for gruppering av eiendommer"
          ></hl-Input>-->
          <!-- v-model="newLandlord.GoPAlias" -!- I have no idea what this is :P -->
        </div>

        <div class="mt-5">
          <button class="btn btn-primary mx-2" @click="saveNewLandlordAndCloseModal" :disabled="invalid">
            Opprett
          </button>
          <button class="btn btn-outline-secondary mx-2" @click="newLandlordModal = false">Avbryt</button>
        </div>
      </ValidationObserver>
    </hl-Modal>
    <!-- Add New Landlord Modal -->
  </PageTemplate>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import BlockContainer from '@/components/BlockContainer.vue';
import PageTemplate from '@/components/PageTemplate.vue';
import List from '@/components/UI/list/List.vue';
import ListHeader from '@/components/UI/list/ListHeader.vue';
import ListItem from '@/components/UI/list/ListItem.vue';
import ListItemCard from '@/components/UI/list/ListItemCard';
import NameInitials from '@/components/UI/NameInitials.vue';
import addValidationRules from '@/mixins/addValidationRules.js';
import Input from '@modules/husleie-framework/src/components/forms/inputs/input/Input.vue';
import SearchCompanies from '@modules/husleie-framework/src/components/forms/inputs/search/SearchCompanies.vue';
import Modal from '@modules/husleie-framework/src/components/modals/Modal.vue';
import i18nCommon from '@modules/i18n/mixins/Common';
import InfoCard from '@/components/InfoCard.vue';

export default {
  name: 'UserLandlordsList',

  components: {
    List,
    ListItem,
    ListItemCard,
    ListHeader,
    NameInitials,
    BlockContainer,
    PageTemplate,
    InfoCard,
    'hl-Modal': Modal,
    'hl-Input': Input,
    'hl-SearchCompanies': SearchCompanies,
  },

  mixins: [addValidationRules, i18nCommon],

  data() {
    return {
      newLandlordModal: false,
      newLandlord: {
        name: '',
        organizationNumber: '',
        addresses: [
          {
            purpose: 'postal',
            street: '',
            careOf: '',
            postalZipcode: '',
            postalArea: '',
          },
        ],
      },
    };
  },

  computed: {
    ...mapGetters(['getWindowWidth', 'isThereAProfessionalLandlord', 'getLandlordProfilesMeta', 'getWhoAmI']),

    showListsItemsAsCards() {
      return this.getWindowWidth < 1000;
    },

    defaultLandlordId() {
      return this.getWhoAmI.preferences.defaultLandlordId;
    },
    translationCompanyName() {
      return this.translate('company name');
    },
  },

  methods: {
    ...mapActions(['createLandlordProfile', 'setLandlordId']),

    async saveNewLandlordAndCloseModal() {
      try {
        await this.createLandlordProfile(this.newLandlord);
        this.newLandlordModal = false;
      } catch (error) {
        this.$toast('Kunne ikke opprette utleierprofil.');
      }
    },

    populateCompany(c) {
      this.newLandlord.name = c.navn;
      this.newLandlord.organizationNumber = c.organisasjonsnummer;

      if ('forretningsadresse' in c) {
        this.newLandlord.addresses[0].street = c.forretningsadresse.adresse;
        this.newLandlord.addresses[0].postalZipcode = c.forretningsadresse.postnummer;
        this.newLandlord.addresses[0].postalArea = c.forretningsadresse.poststed;
      }

      this.$nextTick(() => {
        this.$validator.validateAll();
      });
    },

    fetchPostalArea(e) {
      const axios = require('axios');
      const vue = this;

      vue.newLandlord.addresses[0].postalArea = '';

      // Make a request for a user with a given ID
      if (e.match(/^\d\d\d\d$/)) {
        axios
          .get('/api/poststed/' + e)
          .then(function (response) {
            // handle success
            vue.newLandlord.addresses[0].postalArea = response.data.poststed;
          })
          .catch(function (error) {
            // handle error
            console.error(error);
          });
      }
    },
  },
};
</script>

<style scoped lang="scss">
.initials {
  flex: 0 0 27px;
}

.no-link-appearence {
  color: $dark;
}
</style>
